// React-side entry points for the activity tracker (see ./tracker.ts).
//
// Hosts install the tracker ONCE with what only they know (the collect URL,
// realm, surface, owner/project, an auth-header provider) and then call
// `trackPageView` where a screen/page changes. Everything else — the
// transaction screens' saves, deletes, actions, record opens and searches —
// is captured by the shared components themselves through
// `instrumentTxnRuntime` / `instrumentScreenRuntime`, so no host repeats it.
// All helpers are no-ops until a host has installed the tracker.

import { useEffect } from 'react'
import { activityTrackerRuntime } from './tracker'
import type { ActivityApi, ActivityTrackerConfig } from './tracker'
import type { TxnRuntime } from '../txn/context'
import type { TxnScreenRuntime } from '../txn/screen/contract'

export { activityTrackerRuntime } from './tracker'
export type { ActivityApi, ActivityTrackerConfig } from './tracker'

declare global {
  interface Window { __twastaActivity?: ActivityApi }
}

export function installActivityTracker(cfg: ActivityTrackerConfig): ActivityApi | null {
  if (typeof window === 'undefined') return null
  try {
    return activityTrackerRuntime(window, cfg) as ActivityApi | null
  } catch (e) {
    // Analytics must never take a page down with it.
    console.warn('[activity] tracker failed to start', e)
    return null
  }
}

export function activityTracker(): ActivityApi | null {
  return (typeof window !== 'undefined' && window.__twastaActivity) || null
}

export function trackPageView(name: string, fields?: Record<string, unknown>): void {
  try { activityTracker()?.page(name, fields) } catch { /* never throw from analytics */ }
}

export function trackActivity(event: string, name?: string | null, fields?: Record<string, unknown>): void {
  try { activityTracker()?.track(event, name, fields) } catch { /* never throw from analytics */ }
}

/** Record a page/screen view whenever `name` changes (null/'' = nothing open). */
export function useActivityPageView(name: string | null | undefined, fields?: Record<string, unknown>): void {
  const key = name ? `${name}|${JSON.stringify(fields || {})}` : ''
  useEffect(() => {
    if (name) trackPageView(name, fields)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])
}

// ── instrumentation ───────────────────────────────────────────────────

function statusOf(err: unknown): string {
  const e = err as { response?: { status?: number }; status?: number } | null
  const code = e?.response?.status ?? e?.status
  return code ? String(code) : 'error'
}

/** Time a promise and record it as one event — ok or failed. */
function timed<T>(p: Promise<T>, event: string, name: string, fields: Record<string, unknown>): Promise<T> {
  if (!activityTracker()) return p
  const started = Date.now()
  return p.then(
    (r) => { trackActivity(event, name, { ...fields, status: 'ok', duration_ms: Date.now() - started }); return r },
    (err) => { trackActivity(event, name, { ...fields, status: statusOf(err), duration_ms: Date.now() - started }); throw err },
  )
}

// The approval calls that DECIDE something (the rest of the family reads).
const APPROVAL_DECISIONS = new Set([
  'approval_approve', 'approval_reject', 'approval_bulk', 'approval_withdraw', 'approval_edit',
  'approval_delegate_set', 'approval_delegate_revoke', 'approval_override_set', 'approval_override_delete',
])

const INSTRUMENTED = Symbol.for('twasta.activity.instrumented')

/** The form-stack transport with saves / actions / uploads recorded. */
export function instrumentTxnRuntime(rt: TxnRuntime): TxnRuntime {
  if (!rt || (rt as unknown as Record<symbol, boolean>)[INSTRUMENTED]) return rt
  const out: TxnRuntime = {
    ...rt,
    save: (project, objName, header, txnId, opts) =>
      timed(rt.save(project, objName, header, txnId, opts), 'save', objName,
        { obj_type: 'T', label: txnId ? 'edit' : 'add' }),
    runAction: (project, objName, req) =>
      timed(rt.runAction(project, objName, req), 'action', objName,
        { obj_type: 'T', label: req.actionName }),
    uploadDocument: (file) =>
      timed(rt.uploadDocument(file), 'upload', '', { label: file?.name, size: file?.size }),
  }
  if (rt.runBulkAction) {
    const bulk = rt.runBulkAction
    out.runBulkAction = (project, objName, req) =>
      timed(bulk(project, objName, req), 'bulk_action', objName,
        { obj_type: 'T', label: req.actionName, records: req.txnIds?.length })
  }
  if (rt.openImportAction) {
    const open = rt.openImportAction
    out.openImportAction = (req) => { trackActivity('import', req.objName, { label: req.actionName }); open(req) }
  }
  if (rt.openAssistantAction) {
    const open = rt.openAssistantAction
    out.openAssistantAction = (req) => { trackActivity('action', null, { label: req.title, handler: 'assistant' }); open(req) }
  }
  if (rt.openObjectAction) {
    const open = rt.openObjectAction
    out.openObjectAction = (req) => {
      trackActivity('action', req.sourceObjName, { obj_type: 'T', label: req.actionName, handler: 'open' })
      open(req)
    }
  }
  if (rt.makeReportAdapter) {
    const make = rt.makeReportAdapter
    out.makeReportAdapter = (reportObjName) => { trackActivity('report', reportObjName, { obj_type: 'R' }); return make(reportObjName) }
  }
  ;(out as unknown as Record<symbol, boolean>)[INSTRUMENTED] = true
  return out
}

/** The transaction-screen transport with record opens, saves, deletes,
 *  actions and list searches recorded. */
export function instrumentScreenRuntime(rt: TxnScreenRuntime): TxnScreenRuntime {
  if (!rt || (rt as unknown as Record<symbol, boolean>)[INSTRUMENTED]) return rt
  const out: TxnScreenRuntime = {
    ...rt,
    txn: instrumentTxnRuntime(rt.txn),
    load: (objName, txnId) => timed(rt.load(objName, txnId), 'open_record', objName, { obj_type: 'T', label: txnId }),
    saveBatch: (objName, body) =>
      timed(rt.saveBatch(objName, body), 'save', objName, { obj_type: 'T', label: body.txn_id ? 'edit' : 'add' }),
    remove: (objName, txnId) => timed(rt.remove(objName, txnId), 'delete', objName, { obj_type: 'T', label: txnId }),
    execute: (objName, action, payload) => {
      const p = rt.execute(objName, action, payload)
      const name = (payload as { action_name?: string } | undefined)?.action_name
      if (action === 'run_action') return timed(p, 'action', objName, { obj_type: 'T', label: name })
      if (action === 'run_action_bulk') {
        const ids = (payload as { txn_ids?: unknown[] } | undefined)?.txn_ids
        return timed(p, 'bulk_action', objName, { obj_type: 'T', label: name, records: ids?.length })
      }
      if (APPROVAL_DECISIONS.has(action)) {
        return timed(p, 'action', objName, { obj_type: 'T', label: action.replace(/^approval_/, 'approval: ') })
      }
      return p
    },
    list: (objName, q) => {
      const p = rt.list(objName, q)
      const searched = (q.filters || []).length > 0 && !q.formNo && (q.offset || 0) === 0
      return searched ? timed(p, 'search', objName, { obj_type: 'T', filters: q.filters?.length }) : p
    },
  }
  ;(out as unknown as Record<symbol, boolean>)[INSTRUMENTED] = true
  return out
}
